<template>
  <div
    v-if="digitalizationProjectSettings"
    id="digitalization-uploads-overview"
  >
    <title-panel
      :button-label="t('digitalizationUploadsOverview.newUpload')"
      :count="pageOptions.rows"
      :display-button="isNewUploadAllowed()"
      :on-button-click="handleNewUploadButtonClick"
      :title="t('digitalizationUploadsOverview.uploads')"
    />

    <new-digitalization-upload-modal
      id="new-upload-modal"
      ref="modalRef"
      :digitalization-project-id="digitalizationProjectSettings._id.toString()"
      :digitalization-project-number="digitalizationProjectSettings.number"
      :digitalization-project-title="digitalizationProjectSettings.title"
    />

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :loading="isLoading"
      :items="uploads"
      :mode="TableMode.LAZY"
      :row-class="getRowClass"
      row-clickable
      @row-click="goToDocumentsOverview"
    >
      <template #cell(newAndUpdatedDocumentsCount)="{ data }">
        <div class="upload-content">
          <span v-tooltip.top="t('uploadsOverview.new')">
            {{ data.newAndUpdatedDocumentsCount.new }}
          </span>
          |
          <span v-tooltip.top="t('uploadsOverview.updated')">
            {{ data.newAndUpdatedDocumentsCount.updated }}
          </span>
        </div>
      </template>
      <template #cell(documentsStatuses)="{ data }">
        <div class="document-statuses-list">
          <span
            v-tooltip.top="
              t(
                `digitalizationDocumentStatus.${DocumentStatus.METADATA_INSPECTION}`,
              )
            "
            class="metadata-inspection-count"
            >{{
              data.documentsStatuses[DocumentStatus.METADATA_INSPECTION]
            }}</span
          >
          |
          <span
            v-tooltip.top="
              t(`digitalizationDocumentStatus.${DocumentStatus.FORMAL_CONTROL}`)
            "
            class="formal-control-count"
            >{{ data.documentsStatuses[DocumentStatus.FORMAL_CONTROL] }}</span
          >
          |
          <span
            v-tooltip.top="
              t(`digitalizationDocumentStatus.${DocumentStatus.QUALITY_CHECK}`)
            "
            class="quality-check-count"
            >{{ data.documentsStatuses[DocumentStatus.QUALITY_CHECK] }}</span
          >
          |
          <span
            v-tooltip.top="
              t(
                `digitalizationDocumentStatus.${DocumentStatus.RECORD_LIBRARY_TRANSFER_IN_PROGRESS}`,
              )
            "
            class="transfer-in-progres-count"
            >{{
              data.documentsStatuses[
                DocumentStatus.RECORD_LIBRARY_TRANSFER_IN_PROGRESS
              ]
            }}</span
          >
          <span class="status-separator"></span>
          <span
            v-tooltip.top="
              t(
                `digitalizationDocumentStatus.${DocumentStatus.RECORD_LIBRARY_TRANSFER_SUCCESSFULL}`,
              )
            "
            class="transfer-successsfull-count"
            >{{
              data.documentsStatuses[
                DocumentStatus.RECORD_LIBRARY_TRANSFER_SUCCESSFULL
              ]
            }}</span
          >
          ||
          <span
            v-tooltip.top="
              t(
                `digitalizationDocumentStatus.${DocumentStatus.RECORD_LIBRARY_TRANSFER_FAILED}`,
              )
            "
            class="transfer-failed-count"
            >{{
              data.documentsStatuses[
                DocumentStatus.RECORD_LIBRARY_TRANSFER_FAILED
              ]
            }}</span
          >
        </div>
      </template>
      <template #cell(uploadDate)="{ data }">
        {{ formatDateForDisplay(new Date(data.uploadDate)) }}
      </template>

      <template #cell(action)="{ data }">
        <upload-table-buttons
          :project-id="digitalizationProjectSettings._id"
          :upload="data"
          @loading="setIsLoading"
          @refetch="fetch"
        ></upload-table-buttons>
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import axios, { AxiosResponse } from 'axios';
  import NewDigitalizationUploadModal from '@/components/NewDigitalizationUploadModal/NewDigitalizationUploadModal.vue';
  import TitlePanel from '@/components/TitlePanel/TitlePanel.vue';
  import endpointsList from '@/helpers/endpointsList';
  import {
    DocumentStatus,
    IDigitalizationProjectDetailsDto,
    IDigitalizationUploadSummaryDto,
    IDigitalizationUploadSummaryPageOptionsDto,
    IPageDto,
    Order,
    Role,
  } from 'types';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { formatDateForDisplay } from '@/helpers';
  import { Route, routeParams } from '@/routes';
  import { DigitalizationProjectDetailsTab } from '@/views/DigitalizationProjectDetails/DigitalizationProjectDetails.types';
  import { TABLE_NAMESPACE as DOCUMENTS_TABLE_NAMESPACE } from '../DigitalizationDocumentsOverview/constants';
  import { Types } from 'mongoose';

  const props = defineProps<{
    digitalizationProjectSettings: IDigitalizationProjectDetailsDto;
  }>();
  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();
  const { isUserRole } = useRoles();

  const uploads = ref<IDigitalizationUploadSummaryDto[]>([]);

  const TABLE_NAMESPACE = 'duo';
  const {
    isLoading,
    pageOptions,
    fetch,
    filters,
    filterParams,
    namespaceQueryParams,
  } = useTableWrapper<
    IDigitalizationUploadSummaryDto,
    IDigitalizationUploadSummaryPageOptionsDto
  >(
    TableMode.LAZY,
    'uploadDate',
    getUploads,
    Order.DESC,
    true,
    TABLE_NAMESPACE,
  );

  const tableFields = computed(() =>
    new TableFieldsBuilder<IDigitalizationUploadSummaryDto>({
      labelTranslationKey: 'digitalizationUploadsOverview',
      sortable: true,
    })
      .addField('referenceNumber', {
        sortable: true,
        width: '20%',
      })
      .addField('documentsCount', {
        sortable: true,
        width: '10%',
      })
      .addField('contentTypeName', {
        sortable: true,
        width: '15%',
      })
      .addField('newAndUpdatedDocumentsCount', {
        sortable: false,
        width: '7%',
      })
      .addField('documentsStatuses', {
        sortable: false,
        width: '18%',
      })
      .addField('uploadDate', {
        sortable: true,
        width: '15%',
        valueGetter: (item) => formatDateForDisplay(new Date(item.uploadDate)),
      })
      .addField('checkBotConfig', {
        sortable: false,
        width: '20%',
        label: t('digitalizationUploadsOverview.checkbotConfig'),
        valueGetter: (item) => item.checkBotConfig?.name ?? '-',
      })
      .addField('action' as any, {
        sortable: false,
        width: '7%',
        label: '',
      })
      .build(),
  );

  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      IDigitalizationUploadSummaryDto,
      IDigitalizationUploadSummaryPageOptionsDto
    >({
      translationKey: 'digitalizationUploadsOverview',
    })
      .addInputFilter('referenceNumber', {
        width: '180px',
        customLabel: t('digitalizationUploadsOverview.referenceNumber'),
      })
      .addNumberFilter('documentsCount', {
        width: '170px',
        customLabel: t('digitalizationUploadsOverview.documentsCount'),
      })
      .addInputFilter('contentTypeName', {
        width: '180px',
        customLabel: t('digitalizationUploadsOverview.contentTypeName'),
      })
      .addCheckboxFilter('documentsStatuses', {
        width: '150px',
        customLabel: t('digitalizationUploadsOverview.hideProcessed'),
        filterProperty: 'excludeProcessed',
      })
      .addDateRangeFilter('uploadDate', {
        dateFromKey: 'uploadDateFrom',
        dateToKey: 'uploadDateTo',
        simpleLabel: true,
      })
      .addCheckboxFilter('action' as any, {
        customLabel: t('digitalizationUploadsOverview.showSoftDeleted'),
        filterProperty: 'includeSoftDeleted',
      })
      .build(),
  );

  async function getUploads(): Promise<void> {
    try {
      const response: AxiosResponse<IPageDto<IDigitalizationUploadSummaryDto>> =
        await axios.get(
          endpointsList.digitalizationUploads.getPaginatedDigitalizationUploadsSummary(
            {
              digitalizationProjectId: props.digitalizationProjectSettings
                ._id as unknown as Types.ObjectId,
              order: pageOptions.value.sortOrder,
              page: pageOptions.value.page,
              sortBy: pageOptions.value.sortBy,
              take: pageOptions.value.perPage,
              includeCheckBotConfig: true,
              ...filterParams.value,
            },
          ),
        );
      const pageDto = response.data;
      uploads.value = pageDto.data;
      pageOptions.value.rows = pageDto.meta.itemCount;
    } catch {
      createErrorToast(
        t('digitalizationUploadsOverview.errors.errorGettingUploads.long'),
      );
    }
  }

  function setIsLoading() {
    isLoading.value = true;
  }

  const modalRef = ref<InstanceType<typeof NewDigitalizationUploadModal>>();
  function handleNewUploadButtonClick() {
    modalRef.value?.show();
  }

  function goToDocumentsOverview(row: IDigitalizationUploadSummaryDto) {
    router.push({
      name: Route.DIGITALIZATION_PROJECT_DETAILS,
      params: {
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
          props.digitalizationProjectSettings._id.toString(),
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].tab]:
          DigitalizationProjectDetailsTab.DOCUMENTS,
      },
      query: namespaceQueryParams(
        {
          referenceNumber: row.referenceNumber,
        },
        DOCUMENTS_TABLE_NAMESPACE,
      ),
    });
  }

  function getRowClass(
    upload: IDigitalizationUploadSummaryDto | undefined,
  ): string {
    if (upload) {
      if (upload.isCheckBotInspectionDisabled) {
        return 'selected-row';
      }
    }
    return 'row-class-clickable';
  }

  function isNewUploadAllowed(): boolean {
    return isUserRole([Role.Admin, Role.DigitalizationCoworker]);
  }
</script>

<style scoped lang="scss">
  #digitalization-uploads-overview {
    width: 100%;
    padding: 2rem 0;

    table {
      .upload-content {
        font-size: 0.75rem;
      }

      .document-statuses-list {
        display: flex;
        justify-items: center;
        font-size: 0.75rem;
        column-gap: 0.175rem;

        .status-separator {
          width: 0.5rem;
        }

        .transfer-successsfull-count {
          color: $uploads-overview-accepted-color;
        }

        .transfer-failed-count {
          color: $uploads-overview-rejected-color;
        }
      }
    }
  }
</style>
